import React, { useState } from "react";
import {
  Card,
  List,
  Avatar,
  Input,
  Row,
  Col,
  Button,
  Typography,
  Space,
  Table,
  Tag,
  Segmented,
} from "antd";
import {
  SearchOutlined,
  FileTextOutlined,
  BarChartOutlined,
  DashboardOutlined,
  UserOutlined,
  CalendarOutlined,
  AppstoreOutlined,
  UnorderedListOutlined,
} from "@ant-design/icons";
import { useNavigate, useLocation } from "react-router-dom";
import Head from "../../layout/head/Head";
import Content from "../../layout/content/Content";
import {
  BlockBetween,
  BlockHead,
  BlockHeadContent,
  BlockTitle,
} from "../../components/Component";
import { workOrderReports, Report } from "../../data/reportsData";

const { Title, Text } = Typography;
const { Search } = Input;

interface WorkOrderReportsProps {
  onReportSelect?: (reportId: string) => void;
}

const WorkOrderReports: React.FC<WorkOrderReportsProps> = ({
  onReportSelect,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const flowType = searchParams.get("flowType");
  const flowTypeQuery = flowType ? `?flowType=${flowType}` : "";
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredReports, setFilteredReports] =
    useState<Report[]>(workOrderReports);
  const [viewMode, setViewMode] = useState<"table" | "card">("table");

  const handleSearch = (value: string) => {
    setSearchTerm(value);
    filterReports(value);
  };

  const filterReports = (search: string) => {
    let filtered = workOrderReports;

    if (search) {
      filtered = filtered.filter(
        (report) =>
          report.name.toLowerCase().includes(search.toLowerCase()) ||
          report.description.toLowerCase().includes(search.toLowerCase())
      );
    }

    setFilteredReports(filtered);
  };

  const handleReportClick = (reportId: string) => {
    if (onReportSelect) {
      onReportSelect(reportId);
    }
    // Special routing for contractor-ledger
    if (reportId === "contractor-ledger") {
      navigate(
        `${process.env.PUBLIC_URL}/work-order-reports/contractor-ledger${flowTypeQuery}`
      );
    } else {
      navigate(
        `${process.env.PUBLIC_URL}/work-order-reports/${reportId}${flowTypeQuery}`
      );
    }
  };

  const getIconForCategory = (category: string) => {
    switch (category) {
      case "Master Reports":
        return (
          <DashboardOutlined style={{ fontSize: "24px", color: "#1890ff" }} />
        );
      case "Status Reports":
        return (
          <BarChartOutlined style={{ fontSize: "24px", color: "#52c41a" }} />
        );
      case "Execution Reports":
        return (
          <FileTextOutlined style={{ fontSize: "24px", color: "#fa8c16" }} />
        );
      case "Payment Reports":
        return <UserOutlined style={{ fontSize: "24px", color: "#722ed1" }} />;
      case "Budget Reports":
        return (
          <DashboardOutlined style={{ fontSize: "24px", color: "#f5222d" }} />
        );
      case "Aging Reports":
        return (
          <CalendarOutlined style={{ fontSize: "24px", color: "#faad14" }} />
        );
      default:
        return (
          <FileTextOutlined style={{ fontSize: "24px", color: "#8c8c8c" }} />
        );
    }
  };

  const getCategoryColor = (category: string) => {
    switch (category) {
      case "Master Reports":
        return "#1890ff";
      case "Status Reports":
        return "#52c41a";
      case "Execution Reports":
        return "#fa8c16";
      case "Payment Reports":
        return "#722ed1";
      case "Budget Reports":
        return "#f5222d";
      case "Financial Reports":
        return "#13c2c2";
      case "Aging Reports":
        return "#faad14";
      case "Audit Reports":
        return "#eb2f96";
      default:
        return "#8c8c8c";
    }
  };

  const columns = [
    {
      title: "Report Name",
      dataIndex: "name",
      key: "name",
      width: 300,
      render: (text: string, record: Report) => (
        <a
          onClick={() => handleReportClick(record.id)}
          style={{ color: "#1890ff", cursor: "pointer" }}
        >
          {text}
        </a>
      ),
      sorter: (a: Report, b: Report) => a.name.localeCompare(b.name),
    },
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
      ellipsis: true,
      width: 600,
    },
    {
      title: "Category",
      dataIndex: "category",
      key: "category",
      render: (category: string) => (
        <Tag color={getCategoryColor(category)}>{category}</Tag>
      ),
      filters: [
        { text: "Master Reports", value: "Master Reports" },
        { text: "Status Reports", value: "Status Reports" },
        { text: "Execution Reports", value: "Execution Reports" },
        { text: "Payment Reports", value: "Payment Reports" },
        { text: "Budget Reports", value: "Budget Reports" },
        { text: "Financial Reports", value: "Financial Reports" },
        { text: "Aging Reports", value: "Aging Reports" },
        { text: "Audit Reports", value: "Audit Reports" },
      ],
      onFilter: (value: any, record: Report) => record.category === value,
    },
    {
      title: "Last Updated",
      dataIndex: "lastUpdated",
      key: "lastUpdated",
      sorter: (a: Report, b: Report) =>
        a.lastUpdated.localeCompare(b.lastUpdated),
    },
    {
      title: "Created By",
      dataIndex: "createdBy",
      key: "createdBy",
    },
  ];

  return (
    <>
      <Head title="Work Order Reports" />
      <Content>
        <Card>
          <BlockHead size="lg">
            <BlockBetween>
              <BlockHeadContent>
                <BlockTitle tag="h4">Work Order Reports</BlockTitle>
                <Text type="secondary">
                  Comprehensive reporting suite for work order management and
                  analysis
                </Text>
              </BlockHeadContent>
            </BlockBetween>
          </BlockHead>

          <Row gutter={[24, 24]}>
            <Col span={24}>
              <Space
                size="large"
                style={{ width: "100%", justifyContent: "space-between" }}
              >
                <Search
                  placeholder="Search reports by name or description..."
                  allowClear
                  // enterButton
                  // prefix={<SearchOutlined />}
                  size="large"
                  style={{ width: 400 }}
                  onSearch={handleSearch}
                  onChange={(e) => handleSearch(e.target.value)}
                />
                <Segmented
                  value={viewMode}
                  onChange={(value) => setViewMode(value as "table" | "card")}
                  options={[
                    {
                      label: "Table View",
                      value: "table",
                      icon: <UnorderedListOutlined />,
                    },
                    {
                      label: "Card View",
                      value: "card",
                      icon: <AppstoreOutlined />,
                    },
                  ]}
                />
              </Space>
            </Col>

            <Col span={24}>
              {viewMode === "table" ? (
                <Table
                  columns={columns}
                  dataSource={filteredReports}
                  rowKey="id"
                  pagination={{
                    pageSize: 20,
                    showSizeChanger: true,
                    showTotal: (total, range) =>
                      `${range[0]}-${range[1]} of ${total} reports`,
                  }}
                />
              ) : (
                <List
                  grid={{
                    gutter: 16,
                    xs: 1,
                    sm: 1,
                    md: 2,
                    lg: 2,
                    xl: 3,
                    xxl: 3,
                  }}
                  dataSource={filteredReports}
                  renderItem={(report) => (
                    <List.Item>
                      <Card
                        hoverable
                        size="small"
                        style={{ height: "100%" }}
                        onClick={() => handleReportClick(report.id)}
                        actions={[
                          <Button type="link" size="small">
                            View Report
                          </Button>,
                        ]}
                      >
                        <Card.Meta
                          avatar={
                            <div
                              style={{
                                width: 48,
                                height: 48,
                                borderRadius: "50%",
                                // backgroundColor: getCategoryColor(report.category),
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                              }}
                            >
                              {getIconForCategory(report.category)}
                            </div>
                          }
                          title={
                            <div>
                              <Text strong style={{ fontSize: "14px" }}>
                                {report.name}
                              </Text>
                              <div style={{ marginTop: 4 }}>
                                <Tag color={getCategoryColor(report.category)}>
                                  {report.category}
                                </Tag>
                              </div>
                            </div>
                          }
                          description={
                            <div>
                              <Text
                                style={{ fontSize: "12px", color: "#595959" }}
                              >
                                {report.description}
                              </Text>
                              <div
                                style={{
                                  marginTop: 8,
                                  fontSize: "11px",
                                  color: "#8c8c8c",
                                }}
                              >
                                <Space size="small" split={<span>•</span>}>
                                  <span>Updated: {report.lastUpdated}</span>
                                  <span>{report.createdBy}</span>
                                </Space>
                              </div>
                            </div>
                          }
                        />
                      </Card>
                    </List.Item>
                  )}
                />
              )}
            </Col>
          </Row>
        </Card>
      </Content>
    </>
  );
};

export default WorkOrderReports;
